import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(value: number | null | undefined) {
  if (value === null || value === undefined) return "$0.00";
  
  // Convert from cents to dollars
  const dollars = value / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(dollars);
}

export function capitalizeString(str: string): string {
  if (!str || typeof str !== 'string') return "";
  return str.charAt(0).toUpperCase() + str.toLowerCase().slice(1);
}

export function formatUrl(url: string | undefined): string {
  if (!url || typeof url !== 'string') return "#";
  
  // Check if the URL already starts with http:// or https://
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Add https:// prefix if missing
  return `https://${url}`;
}

export function extractYouTubeId(url: string): string | null {
  if (!url || typeof url !== 'string') return null;
  
  try {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  } catch (e) {
    console.error("Error extracting YouTube ID:", e);
    return null;
  }
}

export function getYouTubeThumbnailUrl(videoId: string, quality: 'default' | 'medium' | 'high' | 'standard' | 'maxres' = 'high'): string {
  const qualityMap = {
    default: 'default',
    medium: 'mqdefault',
    high: 'hqdefault',
    standard: 'sddefault',
    maxres: 'maxresdefault'
  };
  
  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
}

/**
 * Convert YouTube URL to embedded iframe HTML
 */
export function convertYouTubeToEmbed(url: string): string | null {
  const videoId = extractYouTubeId(url);
  if (!videoId) return null;
  
  return `<iframe 
    width="100%" 
    height="315" 
    src="https://www.youtube.com/embed/${videoId}" 
    title="YouTube video player" 
    frameborder="0" 
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
    allowfullscreen
    style="aspect-ratio: 16/9; width: 100%; height: auto;">
  </iframe>`;
}

/**
 * Check if a string is a YouTube URL
 */
export function isYouTubeUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  return /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)/.test(url);
}

/**
 * Cleans a social media handle by removing URL parts and leading @ symbols
 */
export function cleanSocialMediaHandle(handle?: string, platform?: string): string {
  if (!handle || typeof handle !== 'string') return '';
  
  // First, trim spaces
  let cleanedHandle = handle.trim();
  
  // If it's a URL, extract just the username part
  if (cleanedHandle.includes('http')) {
    try {
      const url = new URL(cleanedHandle);
      const pathParts = url.pathname.split('/').filter(Boolean);
      
      if ((platform?.toLowerCase() === 'twitter' || platform?.toLowerCase() === 'x') && pathParts.length > 0) {
        cleanedHandle = pathParts[0];
      } else if (platform?.toLowerCase() === 'instagram' && pathParts.length > 0) {
        cleanedHandle = pathParts[0];
      } else if (platform?.toLowerCase() === 'tiktok' && pathParts.length > 0) {
        cleanedHandle = pathParts[0].replace(/^@/, ''); // Remove @ if present
      } else if (platform?.toLowerCase() === 'facebook') {
        // For Facebook, try to get the username after the domain
        if (pathParts.length > 0) {
          cleanedHandle = pathParts[0];
        }
      } else if (platform?.toLowerCase() === 'youtube') {
        // For YouTube, handle channel and user paths differently
        if (pathParts.length > 0) {
          if (pathParts[0] === 'channel' && pathParts.length > 1) {
            cleanedHandle = pathParts[1];
          } else if (pathParts[0] === 'c' && pathParts.length > 1) {
            cleanedHandle = pathParts[1];
          } else if (pathParts[0] === 'user' && pathParts.length > 1) {
            cleanedHandle = pathParts[1];
          } else if (pathParts[0] === '@') {
            cleanedHandle = pathParts.join('/');
          } else {
            cleanedHandle = pathParts[pathParts.length - 1];
          }
        }
      } else if (platform?.toLowerCase() === 'hudl') {
        // For Hudl, handle both formats: hudl.com/profile and hudl.com/v/ID
        if (pathParts.length > 0) {
          if (pathParts[0] === 'v' && pathParts.length > 1) {
            // Format: hudl.com/v/ID -> keep the ID
            cleanedHandle = pathParts[1];
          } else {
            // Format: hudl.com/profile -> keep the profile
            cleanedHandle = pathParts[pathParts.length - 1];
          }
        }
      } else if (pathParts.length > 0) {
        cleanedHandle = pathParts[pathParts.length - 1];
      }
    } catch (e) {
      // If URL parsing fails, just use the handle as is
      console.error("Error parsing URL:", e);
    }
  }
  
  // Remove leading @ symbol if present for most platforms
  let finalHandle = cleanedHandle.replace(/^@/, '');
  
  // Add @ for TikTok if it doesn't have it - for display purposes only
  if (platform?.toLowerCase() === 'tiktok' && !finalHandle.startsWith('@')) {
    return `@${finalHandle}`;
  }
  
  return finalHandle;
}

/**
 * Format a social media handle for display based on platform conventions
 */
export function formatSocialMediaHandle(handle?: string, platform?: string): string {
  if (!handle || typeof handle !== 'string') return '';
  
  // Remove any URLs and extract just the username part
  const cleanedHandle = cleanSocialMediaHandle(handle, platform);
  
  if (!platform) return cleanedHandle;
  
  switch (platform.toLowerCase()) {
    case 'instagram':
    case 'twitter':
    case 'x':
    case 'tiktok':
      return `@${cleanedHandle.replace(/^@/, '')}`;
    default:
      return cleanedHandle;
  }
}

/**
 * Builds a proper social media URL based on platform and handle
 */
export function buildSocialMediaUrl(platform: string, handle: string): string {
  if (!handle || typeof handle !== 'string') return '#';
  
  // If handle is already a fully formed URL, return it directly
  if (handle.startsWith('http://') || handle.startsWith('https://')) {
    return handle;
  }
  
  // Clean the handle to ensure it's properly formatted
  const cleanHandle = cleanSocialMediaHandle(handle, platform);
  
  // Base URLs for different platforms
  switch (platform.toLowerCase()) {
    case 'instagram':
      return `https://instagram.com/${cleanHandle}`;
      
    case 'twitter':
    case 'x':
      return `https://x.com/${cleanHandle}`;
      
    case 'facebook':
      return `https://facebook.com/${cleanHandle}`;
      
    case 'tiktok': 
      return `https://tiktok.com/@${cleanHandle.replace(/^@/, '')}`;
      
    case 'youtube':
      // For YouTube, check if it's a channel ID or username
      if (cleanHandle.startsWith('UC') && cleanHandle.length > 10) {
        return `https://youtube.com/channel/${cleanHandle}`;
      }
      return `https://youtube.com/${cleanHandle.startsWith('@') ? cleanHandle : '@' + cleanHandle}`;
      
    case 'snapchat':
      return `https://snapchat.com/add/${cleanHandle}`;
      
    case 'linkedin':
      // LinkedIn can be a full profile URL or just the username
      if (cleanHandle.includes('linkedin.com/')) {
        return cleanHandle.startsWith('http') ? cleanHandle : `https://${cleanHandle}`;
      }
      return `https://linkedin.com/in/${cleanHandle}`;
      
    case 'hudl':
      // Support both username and ID formats
      // If cleanHandle looks like a Hudl ID (alphanumeric, typically 6-8 chars), use /v/ format
      // Otherwise, treat as username
      if (/^[a-zA-Z0-9]{4,10}$/.test(cleanHandle) && !cleanHandle.includes(' ')) {
        return `http://www.hudl.com/v/${cleanHandle}`;
      }
      return `https://hudl.com/${cleanHandle}`;
      
    case 'website':
      // For websites, ensure they have proper protocol
      return formatUrl(cleanHandle);
      
    default:
      // For any other platform, try to format as best we can
      return formatUrl(cleanHandle);
  }
}

// Add absoluteUrl function for AthleteProfile.tsx
/**
 * Prefixes a URL with the site URL if it's a relative path
 */
export function absoluteUrl(path: string): string {
  if (!path || typeof path !== 'string') return '';
  
  // If already an absolute URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // Get the base URL from environment or use a default
  const baseUrl = 
    import.meta.env.VITE_APP_URL || 
    import.meta.env.VITE_PUBLIC_URL || 
    window.location.origin;
  
  // Join the base URL and path, ensuring no double slashes
  const joinedPath = `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  
  return joinedPath;
}

// Add this formatDate function if it doesn't already exist
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString || typeof dateString !== 'string') return 'TBD';
  
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  } catch (e) {
    console.error('Error formatting date:', e);
    return 'Invalid Date';
  }
}
